import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { blueprintImage, finishedImage } from "../data/site";

/**
 * Scroll-linked crossfade & scale animation:
 * blueprint / floor-plan slowly transforms into a finished luxury project as the user scrolls.
 * Sticky viewport, driven by useScroll.
 */
export default function BlueprintScroll() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end end"],
    });

    const blueOpacity = useTransform(scrollYProgress, [0, 0.55, 0.9], [1, 0.75, 0]);
    const finishOpacity = useTransform(scrollYProgress, [0.35, 0.65, 1], [0, 0.7, 1]);
    const finishScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
    const gridOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.5, 0.35, 0]);
    const captionY = useTransform(scrollYProgress, [0, 1], [80, -80]);
    const step = useTransform(scrollYProgress, (v) => {
        if (v < 0.25) return "01 — Blueprint";
        if (v < 0.55) return "02 — Structure";
        if (v < 0.8) return "03 — Facade";
        return "04 — Delivered";
    });

    return (
        <section
            ref={ref}
            className="relative bg-[#0b0b0b]"
            style={{ height: "260vh" }}
            data-testid="blueprint-scroll"
        >
            <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
                {/* Blueprint image */}
                <motion.img
                    style={{ opacity: blueOpacity }}
                    src={blueprintImage}
                    alt="Architectural blueprint"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                {/* Gold tint on blueprint */}
                <motion.div
                    style={{ opacity: blueOpacity }}
                    className="absolute inset-0"
                    aria-hidden
                >
                    <div className="absolute inset-0 bg-[#0b0b0b]/70" />
                    <div className="absolute inset-0 bg-[#C89B3C]/10 mix-blend-overlay" />
                </motion.div>

                {/* Finished image */}
                <motion.img
                    style={{ opacity: finishOpacity, scale: finishScale }}
                    src={finishedImage}
                    alt="Finished luxury project"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <motion.div
                    style={{ opacity: finishOpacity }}
                    className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0b0b0b]"
                    aria-hidden
                />

                {/* Blueprint SVG grid overlay */}
                <motion.svg
                    style={{ opacity: gridOpacity }}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                >
                    <defs>
                        <pattern id="bpGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                            <path
                                d="M 40 0 L 0 0 0 40"
                                fill="none"
                                stroke="#C89B3C"
                                strokeWidth="0.5"
                                opacity="0.6"
                            />
                        </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#bpGrid)" />
                </motion.svg>

                {/* Caption */}
                <div className="relative z-10 mx-auto max-w-[1440px] px-6 md:px-10 w-full">
                    <div className="flex items-end justify-between h-full">
                        <motion.div style={{ y: captionY }}>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                                Chapter 04 — From Vision to Landmark
                            </div>
                            <h3 className="mt-6 font-display font-light text-white text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
                                From <em className="text-[#E2C56D]">line</em> on paper
                                <br />
                                to <em className="text-[#E2C56D]">life</em> in Bangalore.
                            </h3>
                            <p className="mt-6 max-w-md text-[#bebebe] text-base font-light leading-relaxed">
                                Every project begins as an idea drawn in graphite — and ends as
                                a place families call home.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Step indicator */}
                <div className="absolute bottom-8 left-6 md:left-10 z-10 flex items-center gap-4">
                    <span className="w-10 h-px bg-[#C89B3C]" />
                    <motion.span className="text-[10px] uppercase tracking-[0.35em] text-[#E2C56D]">
                        {step}
                    </motion.span>
                </div>
                <div className="absolute bottom-8 right-6 md:right-10 z-10 text-[10px] uppercase tracking-[0.35em] text-[#7a7a7a]">
                    Scroll to advance
                </div>
            </div>
        </section>
    );
}
